import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Heart, Mic, Play, Square, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { api, errMessage } from "../../lib/api";

const MAX_NOTE_MS = 15000;

// Cheer Library (Parent Voice Notes): save up to 5 short cheers (max 15s
// each) — Pip surprises the child with a RANDOM one after every win
// (speech ladder, reading, math boss).
export const ParentVoiceNote = ({ child }) => {
  const [notes, setNotes] = useState([]);
  const [maxNotes, setMaxNotes] = useState(5);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const load = () =>
    api.get(`/voice/parent-notes/${child.child_id}`)
      .then(({ data }) => {
        setNotes(data.notes || []);
        setMaxNotes(data.max_notes || 5);
      })
      .catch(() => {});

  useEffect(() => {
    load();
    return () => {
      clearTimeout(timerRef.current);
      audioRef.current?.pause();
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child.child_id]);

  const upload = async (blob, mime) => {
    setBusy(true);
    const form = new FormData();
    form.append("audio", blob, mime.includes("webm") ? "cheer.webm" : "cheer.mp4");
    try {
      await api.post(`/voice/parent-note/${child.child_id}`, form);
      toast.success(`Cheer saved! Pip will surprise ${child.display_name} with one of your cheers.`);
      await load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        upload(new Blob(chunksRef.current, { type: mime }), mime);
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      timerRef.current = setTimeout(() => {
        if (recorder.state === "recording") recorder.stop();
      }, MAX_NOTE_MS);
    } catch {
      toast.error("We couldn't reach your microphone. Check browser permissions and try again.");
    }
  };

  const stopRecording = () => {
    clearTimeout(timerRef.current);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const playNote = (note) => {
    audioRef.current?.pause();
    const a = new Audio(`data:${note.mime};base64,${note.data_b64}`);
    audioRef.current = a;
    a.play().catch(() => {});
  };

  const deleteNote = async (noteId) => {
    setBusy(true);
    try {
      await api.delete(`/voice/parent-note/${child.child_id}/${noteId}`);
      setNotes((ns) => ns.filter((n) => n.note_id !== noteId));
      toast.success("Cheer removed");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const full = notes.length >= maxNotes;

  return (
    <div
      className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 space-y-2.5"
      data-testid={`parent-voice-note-${child.child_id}`}
    >
      <p className="text-sm font-bold font-display flex items-center gap-2">
        <Heart className="h-4 w-4 text-rose-500" aria-hidden="true" />
        Your cheers for {child.display_name}
        <span className="ml-auto text-[11px] font-bold text-rose-600/80" data-testid="voice-note-count">
          {notes.length} of {maxNotes} saved
        </span>
      </p>
      <p className="text-xs text-muted-foreground">
        Save up to {maxNotes} short cheers (15 seconds each) — Pip surprises{" "}
        {child.display_name} with a random one after speech practice, a reading
        win, or a math boss victory.
      </p>
      <div className="rounded-lg bg-background/70 border border-rose-100 px-3 py-2 space-y-1" data-testid="voice-note-prompts">
        <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600/80">
          Need ideas? Try reading one of these:
        </p>
        {[
          `You did it, ${child.display_name}! I'm so proud of you!`,
          `Wow ${child.display_name}, your hard work makes my heart happy!`,
          "Amazing job, superstar! High five when I see you!",
        ].map((line) => (
          <p key={line} className="text-xs italic text-muted-foreground">
            &ldquo;{line}&rdquo;
          </p>
        ))}
      </div>

      {notes.length > 0 && (
        <ul className="space-y-1.5" data-testid="voice-note-list">
          {notes.map((n, i) => (
            <li
              key={n.note_id}
              className="flex items-center gap-2 rounded-lg bg-background/70 border border-rose-100 px-3 py-1.5"
              data-testid={`voice-note-item-${n.note_id}`}
            >
              <span className="text-xs font-bold flex-1">
                Cheer {i + 1}
                <span className="font-normal text-muted-foreground">
                  {" "}· saved {new Date(n.recorded_at).toLocaleDateString()}
                </span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 rounded-full gap-1 px-2 text-xs"
                onClick={() => playNote(n)}
                data-testid={`voice-note-play-${n.note_id}`}
              >
                <Play className="h-3 w-3" aria-hidden="true" /> Listen
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 rounded-full px-2 text-muted-foreground"
                disabled={busy}
                onClick={() => deleteNote(n.note_id)}
                aria-label={`Remove cheer ${i + 1}`}
                data-testid={`voice-note-delete-${n.note_id}`}
              >
                <Trash2 className="h-3 w-3" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {recording ? (
          <Button
            size="sm"
            variant="destructive"
            className="rounded-full gap-1.5"
            onClick={stopRecording}
            data-testid="voice-note-stop-button"
          >
            <Square className="h-3.5 w-3.5" aria-hidden="true" /> Stop &amp; save
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full gap-1.5"
            disabled={busy || full}
            onClick={startRecording}
            data-testid="voice-note-record-button"
          >
            <Mic className="h-3.5 w-3.5" aria-hidden="true" />
            {notes.length ? "Add another cheer" : "Record a cheer"}
          </Button>
        )}
        {recording && (
          <span className="text-xs font-semibold text-rose-600 animate-pulse" data-testid="voice-note-recording-hint">
            Recording… speak your cheer!
          </span>
        )}
        {full && !recording && (
          <span className="text-xs text-muted-foreground" data-testid="voice-note-full-hint">
            Library full — remove a cheer to add a new one.
          </span>
        )}
      </div>
    </div>
  );
};
